import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Button } from '../../../shared/components/Button';

interface Props {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal confirmation. Rendered only while open: on mount it remembers the previously focused control and
 * moves focus into the dialog (to the safe "keep" action); Tab/Shift+Tab are trapped inside; Escape cancels;
 * on unmount focus returns to where it was. Same visual language as business-profile-setup's ConfirmDialog,
 * but with real focus handling (that component has none).
 */
export function PhiConfirmDialog({ title, body, confirmLabel, cancelLabel, busy = false, onConfirm, onCancel }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    cancelRef.current?.focus();
    return () => previous?.focus();
  }, []);

  // While busy both buttons are disabled; a disabled control cannot hold focus, so the dialog container takes it
  // and focus stays inside the modal instead of falling back to <body>.
  useEffect(() => {
    if (busy) dialogRef.current?.focus();
  }, [busy]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && !busy) {
      event.stopPropagation();
      onCancel();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="phi-dialog-backdrop" role="presentation">
      <div
        ref={dialogRef}
        className="phi-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="phi-dialog-title"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h3 id="phi-dialog-title">{title}</h3>
        <div>{body}</div>
        <div className="phi-dialog-actions">
          <button ref={cancelRef} type="button" className="phi-btn-secondary" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <Button type="button" className="phi-btn-danger" onClick={onConfirm} loading={busy} loadingLabel="Canceling…">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
